
import React, { useState, useEffect } from "react";
import {
  Eye,
  Wallet,
  AlertCircle,
  CheckCircle,
  Minus,
  Plus,
  UserX,
  RotateCcw,
} from "lucide-react";

import StatusBadge from "./StatusBadge";
import { getThHeaderStyle, getTdStyle } from "../../../(Admin)Attendance/styles/attendanceStyles";
import { useVisibleColumns } from "../../../../hooks/useVisibleColumns";
import Pagination from "../../../../components/Pagination";

const PayrollTable = ({
  t,
  styles,
  theme,
  language,
  currentTheme,
  fonts,
  spacing,
  screenSize,
  currentItems,
  hasMore,
  visibleItems,
  filteredPayrollData,
  togglingIgnoredIds,
  unpayingIds,
  modulePermissions,
  hasRowActions,
  setSelectedEmployee,

  setDeductionModal,
  setAllowanceModal,
  handleToggleIsIgnored,

  handleUnpayPayroll,
  handleShowMore,
  setDeductionTooltip,
  showAlert,
  profile,
  selectionEnabled,
  selectedIds,
  onToggleSelect,
}) => {
  const isAdmin = profile?.role === "admin" || profile?.role === "superadmin";

  // ✅ Admin-level column visibility (hiddenColumns registry). Bypassed roles
  // (admin/superadmin/managers with allModulesAllowed) always see every
  // column — the hook handles that, so their view is unchanged.
  const payrollColumns = [
    { key: "employee" },
    { key: "basicSalary" },
    { key: "allowances" },
    { key: "grossSalary" },
    { key: "deductions" },
    { key: "netSalary" },
    { key: "status" },
    { key: "actions" },
  ];
  const visibleColumns = useVisibleColumns("payroll", "payrollTable", payrollColumns);
  const showColumn = (key) => visibleColumns.some((col) => col.key === key);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredPayrollData]);

  const totalPages = Math.ceil((filteredPayrollData?.length || 0) / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const displayedPayroll = (filteredPayrollData || []).slice(
    startIdx,
    startIdx + itemsPerPage
  );

  const showRowActions =
    isAdmin ||
    (hasRowActions &&
      (modulePermissions?.employeeDetails ||
        modulePermissions?.employeeDeductions ||
        modulePermissions?.employeeAllowances ||
        modulePermissions?.ignoreAttendance ||
        (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll)));

  const renderIgnoreAttendanceButton = (employee) => {
    const isToggling = togglingIgnoredIds.has(employee.id);
    const isIgnored = employee.isIgnored;

    return (
      <button
        onClick={() => handleToggleIsIgnored(employee.id)}
        disabled={isToggling}
        title={employee.isIgnored ? "Unignore attendance" : "Ignore attendance"}
        style={btnStyle({ theme, variant: "neutral", isIgnored, disabled: isToggling })}
        onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral", isIgnored, disabled: isToggling })}
        onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral", isIgnored, disabled: isToggling })}
      >
        <UserX size={14} />
      </button>
    );
  };

  const renderUnpayButton = (employee) => {
    const isUnpaying = unpayingIds.has(employee.id);
    const isPaid = employee.status === "paid";

    if (!isPaid) return null;

    const handleUnpayClick = () => {
      if (window.confirm(`${t("unpayConfirm")} (${employee.employeeName})`)) {
        handleUnpayPayroll(employee.id, {
          onSuccess: () => {
            if (showAlert) {
              showAlert(t("success"), t("unpaySuccess"), "success");
            }
          },
          onError: (err) => {
            if (showAlert) {
              showAlert(
                t("error"),
                t("unpayError") + ": " + err.message,
                "error",
              );
            }
          },
        });
      }
    };

    return (
      <button
        onClick={handleUnpayClick}
        disabled={isUnpaying}
        title={t("unpayPayroll")}
        style={btnStyle({ theme, variant: "neutral", disabled: isUnpaying })}
        onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral", disabled: isUnpaying })}
        onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral", disabled: isUnpaying })}
      >
        <RotateCcw size={14} />
      </button>
    );
  };

  return (
    <div>
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "700",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexDirection: "row",
          textAlign: language === "ar" ? "right" : "left",
          color: currentTheme.colors.textPrimary,
        }}
      >
        <Wallet size={20} />
        {language === "ar" ? "قائمة المرتبات" : "Payroll List"}
      </h3>
      <div style={styles.tableContainer}>
        <table
          style={{
            ...styles.table,
            minWidth:
              screenSize === "xs"
                ? "600px"
                : screenSize === "sm"
                  ? "700px"
                  : "900px",
          }}
        >
          <thead>
            <tr style={styles.tableHeader}>
              {selectionEnabled && (
                <th style={{ ...styles.tableHeader, width: "36px", padding: "0.75rem 0.5rem" }} />
              )}
              {showColumn("employee") && (
                <th
                  style={{
                    ...styles.tableHeader,
                    textAlign: language === "ar" ? "right" : "left",
                  }}
                >
                  {t("employee")}
                </th>
              )}
              {showColumn("basicSalary") && (
                <th style={styles.tableHeader}>{t("basicSalary")}</th>
              )}
              {showColumn("allowances") && (
                <th style={styles.tableHeader}>{t("allowances")}</th>
              )}
              {showColumn("grossSalary") && (
                <th style={styles.tableHeader}>{t("grossSalary")}</th>
              )}
              {showColumn("deductions") && (
                <th style={styles.tableHeader}>{t("deductions")}</th>
              )}
              {showColumn("netSalary") && (
                <th style={styles.tableHeader}>{t("netSalary")}</th>
              )}
              {showColumn("status") && (
                <th style={styles.tableHeader}>{t("status")}</th>
              )}
              {showColumn("actions") && showRowActions && (
                <th style={{ ...styles.tableHeader, textAlign: "center" }}>
                  {t("actions")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {displayedPayroll.map((employee) => (
              <tr
                key={employee.id}
                style={{
                  background: currentTheme.colors.cardBackground,
                  borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              >
                {selectionEnabled && (
                  <td
                    style={{ ...styles.tableCell, width: "36px", padding: "0.75rem 0.5rem", textAlign: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(employee.id) || false}
                      onChange={() => onToggleSelect?.(employee.id)}
                      title={t("selectEmployeeRow")}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        accentColor: currentTheme.colors.primary,
                      }}
                    />
                  </td>
                )}
                {showColumn("employee") && (
                  <td style={styles.tableCell}>
                    <div style={{ display: "flex", alignItems: "Left", gap: "16px" }}>
                      <div>
                        <div
                          style={{
                            color: currentTheme.colors.textPrimary,
                            fontSize: "15px",
                            fontWeight: "700",
                            fontFamily: "monospace",
                            alignItems: "Left",
                            marginBottom: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {employee.employeeName}
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                {showColumn("basicSalary") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {Math.round(employee.basicSalary).toLocaleString()} {t("currency")}
                    </div>
                  </td>
                )}
                {showColumn("allowances") && (
                  <td style={{ ...styles.tableCell, whiteSpace: "nowrap" }}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {`+${Math.round(Number(employee.totalAllowances) || 0).toLocaleString()} ${t("currency")}`}
                    </div>
                    <div style={{ fontSize: "11px", color: currentTheme.colors.textMuted, marginTop: "2px" }}>
                      {t("includingOvertime")}
                    </div>
                  </td>
                )}
                {showColumn("grossSalary") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {Math.round(employee.grossSalary).toLocaleString()} {t("currency")}
                    </div>
                  </td>
                )}
                {showColumn("deductions") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.danger, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {`-${Math.round(employee.totalDeductions).toLocaleString()} ${t("currency")}`}
                    </div>
                    <div style={{ fontSize: "11px", color: currentTheme.colors.textMuted, marginTop: "2px" }}>
                      {t("taxInsuranceOther")}
                    </div>
                  </td>
                )}
                {showColumn("netSalary") && (
                  <td style={styles.tableCell}>
                    <div style={{ color: currentTheme.colors.textPrimary, fontSize: "15px", fontWeight: "700", fontFamily: "monospace" }}>
                      {Math.round(employee.netSalary).toLocaleString()} {t("currency")}
                    </div>
                  </td>
                )}
                {showColumn("status") && (
                  <td style={styles.tableCell}>
                    <StatusBadge status={employee.status} language={language} t={t} />
                    {employee.paidDate && (
                      <div style={{ fontSize: "11px", color: currentTheme.colors.textMuted, marginTop: "4px" }}>
                        {new Date(employee.paidDate).toLocaleDateString("en-US")}
                      </div>
                    )}
                  </td>
                )}
                {showColumn("actions") && showRowActions && (
                  <td style={{ ...styles.tableCell, position: "relative", minWidth: "180px" }}>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                      {(isAdmin || modulePermissions?.employeeDetails) && (
                        <button
                          style={btnStyle({ theme, variant: "neutral" })}
                          onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
                          onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
                          onClick={() => setSelectedEmployee(employee)}
                          title={t("viewDetails")}
                        >
                          <Eye size={14} />
                        </button>
                      )}
                      {(isAdmin || modulePermissions?.employeeDeductions) && (
                        <button
                          style={btnStyle({ theme, variant: "neutral" })}
                          onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
                          onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
                          onClick={() => setDeductionModal({ isOpen: true, employee })}
                          title={t("deductions")}
                        >
                          <Minus size={14} color="#ef4444" />
                        </button>
                      )}
                      {(isAdmin || modulePermissions?.employeeAllowances) && (
                        <button
                          style={btnStyle({ theme, variant: "neutral" })}
                          onMouseEnter={(e) => hoverIn(e, { theme, variant: "neutral" })}
                          onMouseLeave={(e) => hoverOut(e, { theme, variant: "neutral" })}
                          onClick={() => setAllowanceModal({ isOpen: true, employee })}
                          title={t("allowances")}
                        >
                          <Plus size={14} color="#10b981" />
                        </button>
                      )}
                      {(modulePermissions?.ignoreAttendance || isAdmin) && renderIgnoreAttendanceButton(employee)}
                      {/* Unpay is gated by the registry's unpayPayroll key
                          (falls back to processPayroll on legacy permission
                          payloads that predate the key). */}
                      {(isAdmin || (modulePermissions?.unpayPayroll ?? modulePermissions?.processPayroll)) && renderUnpayButton(employee)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          theme={theme}
          isRTL={language === "ar"}
        />
      </div>
    </div>
  );
};

function btnStyle({ theme, variant, isIgnored, disabled }) {
  const isDark = theme === "dark";

  const variants = {
    neutral: {
      bg: isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)",
      color: isDark ? "#f1f5f9" : "#334155",
    },
    danger: {
      bg: isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)",
      color: isDark ? "#fca5a5" : "#b91c1c",
    },
    success: {
      bg: isDark ? "rgba(16,185,129,0.25)" : "rgba(209,250,229,0.9)",
      color: isDark ? "#10b981" : "#059669",
    },
  };

  let v = variants[variant] || variants.neutral;
  if (isIgnored) v = variants.danger;

  return {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "none",
    background: v.bg,
    color: v.color,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
  };
}

function hoverIn(e, { theme, variant, isIgnored, disabled }) {
  if (disabled) return;
  const isDark = theme === "dark";
  const activeVariant = isIgnored ? "danger" : variant;

  if (activeVariant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.4)" : "rgba(254,202,202,0.9)";
  } else if (activeVariant === "success") {
    e.currentTarget.style.background = isDark ? "rgba(16,185,129,0.4)" : "rgba(167,243,208,0.9)";
  } else {
    e.currentTarget.style.background = isDark ? "rgba(71,85,105,0.8)" : "rgba(203,213,225,0.8)";
  }
  e.currentTarget.style.transform = "translateY(-2px)";
}

function hoverOut(e, { theme, variant, isIgnored, disabled }) {
  if (disabled) return;
  const isDark = theme === "dark";
  const activeVariant = isIgnored ? "danger" : variant;

  if (activeVariant === "danger") {
    e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.25)" : "rgba(254,226,226,0.9)";
  } else if (activeVariant === "success") {
    e.currentTarget.style.background = isDark ? "rgba(16,185,129,0.25)" : "rgba(209,250,229,0.9)";
  } else {
    e.currentTarget.style.background = isDark ? "rgba(71,85,105,0.6)" : "rgba(226,232,240,0.8)";
  }
  e.currentTarget.style.transform = "translateY(0)";
}

export default PayrollTable;
